import * as React from "react";
import cs from "classnames";
import PropTypes from "prop-types";
import { LinkGenerator, Icon, Button } from "@lululemon/ui-library";
import { rightLinkArrowIcon, circleCheckmarkIcon } from "@lululemon/ui-icons";
import { translatePills } from "../../utils/bra-quiz";
import styles from "./bra-quiz-bar.module.scss";

const BraQuizBar = ({
  cdpHorizontalFilters,
  contentfulData,
  isVisible,
  result,
  variant,
  truefitAtCDPABFlag
}) => {
  const barData = contentfulData?.find(
    (element) => element.fields.variant === variant
  )?.fields;
  const url = barData?.callToAction?.fields.webRoute;
  const linkLabel = barData?.callToAction?.fields.callToAction;
  const trueFitTitle = barData?.title?.replace("lululemon", "True Fit");
  const caption = truefitAtCDPABFlag ? trueFitTitle : barData?.title;
  const [screenReadersCaption, setScreenReadersCaption] = React.useState("");
  const getTextPill = translatePills(barData?.sizeTexts);

  React.useEffect(() => {
    setTimeout(() => {
      setScreenReadersCaption(`${{ caption }} ${{ result }}`);
    }, 1000);
  }, []);

  const formatBraQuizResults = () => {
    if (!result || !Object.keys(result).length) {
      return null;
    }
    const { bandSize, letterSizeLabel, numberedSizeLabel } = result;
    const legend = [];
    if (!!bandSize && Object.keys(bandSize).length) legend.push(bandSize);
    if (!!numberedSizeLabel && Object.keys(numberedSizeLabel).length) {
      legend.push(getTextPill(numberedSizeLabel));
    }
    if (!!letterSizeLabel && Object.keys(letterSizeLabel).length)
      legend.push(getTextPill(letterSizeLabel));
    return legend.join(" | ");
  };

  if (variant === "mobile-cta") {
    return (
      <div
        className={cs(styles.stickyBraQuiz, {
          [styles.stickyBraQuizHidden]: !isVisible
        })}
        data-testid="bra-quiz-sticky"
      >
        <Button
          className={cs(styles.stickyBraQuizButton, {
            "tfc-fitrec-register": truefitAtCDPABFlag
          })}
          data-category={truefitAtCDPABFlag ? "Bras" : null}
          data-locale={truefitAtCDPABFlag ? "en_US" : null}
          data-lulu-track="bra-quiz-cta-bottom"
          href={truefitAtCDPABFlag ? null : url}
          kind="primary"
          label={linkLabel}
          mobileBlock
          tag={truefitAtCDPABFlag ? "div" : LinkGenerator}
          visible
        >
          {linkLabel}
        </Button>
        <div className={cs("lll-text-body-3", styles.stickyBraQuizText)}>
          {caption}
        </div>
      </div>
    );
  }

  const formattedResult = formatBraQuizResults();

  return (
    <div
      className={cs(styles.braQuizBar, {
        [styles.braQuizBarResults]: variant === "desktop-results",
        [styles.braQuizBarVisible]: isVisible,
        [styles.braQuizBarHide]: !isVisible,
        [styles.braQuizWidth]: cdpHorizontalFilters
      })}
      data-testid={
        variant === "desktop-cta" ? "bra-quiz-bar" : "bra-quiz-bar-results"
      }
    >
      <span className={styles.braQuizBarIconBox}>
        <Icon
          content={circleCheckmarkIcon}
          className={cs(styles.circleCheckmarkIcon, {
            [styles.circleCheckmarkIconWithResults]: !!Object.keys(result || {})
              .length
          })}
        />
      </span>
      <div className={cs("lll-text-body-2", styles.braQuizBarContentBox)}>
        {variant === "desktop-cta" ? (
          <div className={styles.braQuizBarCaption}>{caption}</div>
        ) : (
          <>
            <span className="sr-only" aria-live="polite">
              {screenReadersCaption}
            </span>
            <div aria-hidden="true" className={styles.braQuizBarCaption}>
              {caption}
              &nbsp;
              {formattedResult && (
                <span
                  className={cs(
                    "lll-font-weight-semibold",
                    styles.braQuizBarCaptionSize
                  )}
                >
                  {formattedResult}
                </span>
              )}
            </div>
          </>
        )}
        {truefitAtCDPABFlag ? (
          <div
            className={`tfc-fitrec-register ${styles.braQuizBarCTAWrapper} ${styles.braQuizBarCTAWrapperTruefit}`}
            data-locale="en_US"
            data-category="Bras"
          >
            <p className={styles.braQuizBarCTALabel}>{linkLabel}</p>
            <Icon
              content={rightLinkArrowIcon}
              className={styles.braQuizBarCTAIcon}
            />
          </div>
        ) : (
          <LinkGenerator
            href={url}
            className={styles.braQuizBarCTA}
            analyticsAttrs={{ "data-lulu-track": "bra-quiz-cta-bottom" }}
          >
            <div className={styles.braQuizBarCTAWrapper}>
              <p className={styles.braQuizBarCTALabel}>{linkLabel}</p>
              <Icon
                content={rightLinkArrowIcon}
                className={styles.braQuizBarCTAIcon}
              />
            </div>
          </LinkGenerator>
        )}
      </div>
    </div>
  );
};

BraQuizBar.propTypes = {
  contentfulData: PropTypes.arrayOf(PropTypes.object),
  isVisible: PropTypes.bool,
  result: PropTypes.object,
  variant: PropTypes.string,
  truefitAtCDPABFlag: PropTypes.bool
};

BraQuizBar.defaultProps = {
  contentfulData: null,
  isVisible: true,
  result: {},
  variant: null,
  truefitAtCDPABFlag: false
};

export default BraQuizBar;
